/**
 * ViewerLayoutManager - Viewer-specific layout logic
 *
 * Handles responsive layout calculations for the viewer canvas,
 * separate from the editor's fixed viewport approach.
 */

import { useMemo, useState, useEffect, useCallback, useLayoutEffect, type RefObject } from 'react';
import { Component, Viewport } from '@/types';

interface ContentBounds {
  width: number;
  height: number;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

interface ViewerLayoutConfig {
  containerWidth: number;
  contentBounds: ContentBounds;
  viewport: Viewport;
  components: Component[];
}

const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

const measureElement = (element: HTMLElement): { width: number; height: number } => {
  const rect = element.getBoundingClientRect();

  return {
    width: rect.width || element.clientWidth || 0,
    height: rect.height || element.clientHeight || 0,
  };
};

/**
 * Calculate the actual bounds of all components
 */
export const calculateContentBounds = (components: Component[]): ContentBounds => {
  if (components.length === 0) {
    return {
      width: 0,
      height: 0,
      minX: 0,
      minY: 0,
      maxX: 0,
      maxY: 0,
    };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  components.forEach((component) => {
    // Skip components without position or size (e.g., children in flex containers)
    if (!component.position || !component.size) {
      return;
    }

    const width = typeof component.size.width === 'number' ? component.size.width : 0;
    const height = typeof component.size.height === 'number' ? component.size.height : 0;

    // Skip components with non-numeric dimensions
    if (width === 0 || height === 0) {
      return;
    }

    const left = component.position.x ?? 0;
    const top = component.position.y ?? 0;
    const right = left + width;
    const bottom = top + height;

    minX = Math.min(minX, left);
    minY = Math.min(minY, top);
    maxX = Math.max(maxX, right);
    maxY = Math.max(maxY, bottom);
  });

  return {
    width: maxX - minX,
    height: maxY - minY,
    minX,
    minY,
    maxX,
    maxY,
  };
};

/**
 * Calculate responsive scale based on container width and content
 */
export const calculateResponsiveScale = (
  containerWidth: number,
  contentWidth: number,
  viewport: Viewport
): number => {
  // For very wide screens, don't scale up beyond 100%
  if (containerWidth >= contentWidth) {
    return 1;
  }

  // Never scale up, only scale down when necessary
  if (containerWidth >= contentWidth) {
    return 1;
  }

  // Calculate scale to fit content within container
  const scale = containerWidth / contentWidth;

  // Apply minimum scale limits based on viewport
  const minScale = viewport === 'mobile' ? 0.5 : 0.65;

  return Math.max(scale, minScale);
};

/**
 * Determine if content should be constrained to max width for readability
 */
export const shouldConstrainWidth = (
  containerWidth: number,
  contentWidth: number,
  components: Component[]
): boolean => {
  // Check if any root component has full-width responsive settings
  const hasFullWidthComponent = components.some(component => {
    if (!component.parentId) { // Root component
      const responsiveWidth = component.responsivePosition?.responsiveSize?.width;
      return responsiveWidth === '100%' || responsiveWidth === '100vw';
    }
    return false;
  });

  // If we have full-width components, don't constrain
  if (hasFullWidthComponent) {
    return false;
  }

  // For reading-focused content, constrain max width
  // This can be toggled based on content type or user preference
  const MAX_READABLE_WIDTH = 1200;

  return containerWidth > MAX_READABLE_WIDTH && contentWidth < MAX_READABLE_WIDTH;
};

/**
 * Hook for managing viewer canvas layout
 */
export const useViewerLayout = (config: ViewerLayoutConfig) => {
  const { containerWidth, contentBounds } = config;

  // Calculate responsive dimensions
  const dimensions = useMemo(() => {
    // Base dimensions on actual content, not fixed viewport
    const contentWidth = contentBounds.width || 1920; // Fallback to default
    const contentHeight = contentBounds.height || 1080;

    // Never constrain width in viewer - always use full container width
    const effectiveWidth = containerWidth;
    const constrainWidth = false; // Disable all width constraints

    // Don't scale in viewer - components should handle their own responsive sizing
    const scale = 1;

    // Calculate final dimensions
    const scaledWidth = effectiveWidth;
    const scaledHeight = contentHeight;

    return {
      contentWidth,
      contentHeight,
      containerWidth: effectiveWidth,
      scale,
      scaledWidth,
      scaledHeight,
      constrainWidth,
    };
  }, [containerWidth, contentBounds]);

  // Determine layout strategy based on screen size
  const layoutStrategy = useMemo(() => {
    if (containerWidth < 768) {
      return 'mobile'; // Stack and reflow
    } else if (containerWidth < 1200) {
      return 'tablet'; // Scale to fit
    } else {
      return 'desktop'; // Natural size or slight scale
    }
  }, [containerWidth]);

  // Calculate canvas styles
  const canvasStyles = useMemo(() => {
    return {
      container: {
        width: '100%',
        maxWidth: 'none', // Never constrain width in viewer
        margin: '0',
        position: 'relative' as const,
        // Don't set overflow here - let parent container handle scrolling
      },
      canvas: {
        width: '100%', // Always use full width
        transform: 'none', // Remove scaling completely
        transformOrigin: 'top left',
        position: 'relative' as const,
      },
      wrapper: {
        width: '100%',
        position: 'relative' as const,
        display: 'flex',
        justifyContent: 'stretch',
        alignItems: 'flex-start',
      }
    };
  }, []);

  return {
    dimensions,
    layoutStrategy,
    canvasStyles,
    contentBounds,
  };
};

/**
 * Hook for monitoring container dimensions
 */
export const useContainerDimensions = (containerRef: RefObject<HTMLElement>) => {
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });

  const updateDimensions = useCallback((nextDimensions: { width: number; height: number }) => {
    setDimensions((currentDimensions) => {
      if (
        currentDimensions.width === nextDimensions.width &&
        currentDimensions.height === nextDimensions.height
      ) {
        return currentDimensions;
      }

      return nextDimensions;
    });
  }, []);

  useIsomorphicLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    updateDimensions(measureElement(element));

    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        updateDimensions({ width, height });
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, updateDimensions]);

  return dimensions;
};

/**
 * Hook for handling responsive breakpoints
 */
export const useViewerBreakpoints = (width: number) => {
  return useMemo(() => ({
    isMobile: width < 768,
    isTablet: width >= 768 && width < 1200,
    isDesktop: width >= 1200,
    isWidescreen: width >= 1920,
  }), [width]);
};

export default {
  calculateContentBounds,
  calculateResponsiveScale,
  useViewerLayout,
  useContainerDimensions,
  useViewerBreakpoints,
};
